import { Link, useParams } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { fmtDurationMs, api, formatSerial, testResultLabel, testResultPillClass, testTypeLabel } from "../lib/api.ts";
import { useSortableTable, SortHeader } from "../components/SortableTable.tsx";

export function StaffOrgDetailPage() {
  const { orgId } = useParams<{ orgId: string }>();

  const orgs = useQuery({
    queryKey: ["staff", "orgs"],
    queryFn: api.staffListOrgs,
  });
  const org = orgs.data?.orgs.find((o) => o.id === orgId);

  const members = useQuery({
    queryKey: ["staff", "orgs", orgId, "members"],
    queryFn: () => api.staffListOrgMembers(orgId!),
    enabled: !!orgId,
  });
  const devices = useQuery({
    queryKey: ["staff", "orgs", orgId, "devices"],
    queryFn: () => api.staffListDevices(orgId!),
    enabled: !!orgId,
  });
  const tests = useQuery({
    queryKey: ["staff", "orgs", orgId, "tests", 200],
    queryFn: () => api.staffListTests(orgId!, 200),
    enabled: !!orgId,
  });

  // Sort tables independently — each one keeps its own sort state.
  const memberSort = useSortableTable(members.data?.members ?? [], {
    email:     (m) => m.email,
    name:      (m) => m.name,
    role:      (m) => m.role,
    createdAt: (m) => m.createdAt,
    blocked:   (m) => (m.blockedAt ? 1 : 0),
  }, "createdAt", "desc");

  const deviceSort = useSortableTable(devices.data?.devices ?? [], {
    name:            (d) => d.name,
    assetSerial:     (d) => d.assetSerial,
    serial:          (d) => d.serial,
    updateChannel:   (d) => d.updateChannel,
    firmwareVersion: (d) => d.firmwareVersion,
    pairedAt:        (d) => d.pairedAt,
    lastSeen:        (d) => d.lastSeen,
  }, "pairedAt", "desc");

  const testSort = useSortableTable(tests.data?.tests ?? [], {
    startedAt:    (t) => t.startedAt,
    testType:     (t) => testTypeLabel(t.testType),
    testResult:   (t) => testResultLabel(t.testResult),
    durationMs:   (t) => t.durationMs,
    deviceId:     (t) => t.deviceId,
  }, "startedAt", "desc");

  return (
    <>
      <div style={{ marginBottom: "1rem" }}>
        <Link to="/staff">← All organisations</Link>
      </div>

      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", flexWrap: "wrap", gap: "0.5rem" }}>
        <h1 style={{ margin: 0 }}>{org?.name ?? <span className="muted">Loading…</span>}</h1>
        {org?.blockedAt && (
          <span className="pill fail" title={org.blockedReason ?? undefined}>Blocked</span>
        )}
      </div>

      {org && (
        <div className="card">
          <div className="kv-grid">
            <span className="muted">Slug</span>
            <code>{org.slug ?? "—"}</code>
            <span className="muted">Created</span>
            <span>{new Date(org.createdAt).toLocaleString()}</span>
            <span className="muted">Members</span>
            <span>{members.data?.members.length ?? "…"}</span>
            <span className="muted">Devices</span>
            <span>{org.deviceCount}</span>
            <span className="muted">Tests</span>
            <span>{org.testCount}</span>
          </div>
        </div>
      )}

      <div className="card">
        <h3>Members ({members.data?.members.length ?? "…"})</h3>
        {members.isLoading && <p className="muted">Loading…</p>}
        {members.error && (
          <p className="err">Couldn't load — {(members.error as Error).message}</p>
        )}
        {members.data && members.data.members.length === 0 && (
          <p className="muted">No members.</p>
        )}
        {members.data && members.data.members.length > 0 && (
          <div className="table-scroll">
            <table>
              <thead>
                <tr>
                  <SortHeader label="Email"    columnKey="email"     sortKey={memberSort.sortKey} sortDir={memberSort.sortDir} onSort={memberSort.handleSort} />
                  <SortHeader label="Name"     columnKey="name"      sortKey={memberSort.sortKey} sortDir={memberSort.sortDir} onSort={memberSort.handleSort} />
                  <SortHeader label="Role"     columnKey="role"      sortKey={memberSort.sortKey} sortDir={memberSort.sortDir} onSort={memberSort.handleSort} />
                  <SortHeader label="Joined"   columnKey="createdAt" sortKey={memberSort.sortKey} sortDir={memberSort.sortDir} onSort={memberSort.handleSort} />
                  <SortHeader label="Status"   columnKey="blocked"   sortKey={memberSort.sortKey} sortDir={memberSort.sortDir} onSort={memberSort.handleSort} />
                </tr>
              </thead>
              <tbody>
                {memberSort.sorted.map((m) => (
                  <tr key={m.id} style={m.blockedAt ? { opacity: 0.6 } : undefined}>
                    <td>{m.email ?? <span className="muted">—</span>}</td>
                    <td>{m.name ?? <span className="muted">—</span>}</td>
                    <td>{m.role}</td>
                    <td>{new Date(m.createdAt).toLocaleDateString()}</td>
                    <td>
                      {m.blockedAt
                        ? <span className="pill fail" title={m.blockedReason ?? undefined}>Blocked</span>
                        : <span className="muted">Active</span>}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      <div className="card">
        <h3>Devices ({devices.data?.devices.length ?? "…"})</h3>
        {devices.isLoading && <p className="muted">Loading…</p>}
        {devices.error && (
          <p className="err">Couldn't load — {(devices.error as Error).message}</p>
        )}
        {devices.data && devices.data.devices.length === 0 && (
          <p className="muted">No devices paired.</p>
        )}
        {devices.data && devices.data.devices.length > 0 && (
          <div className="table-scroll">
            <table>
              <thead>
                <tr>
                  <SortHeader label="Name"         columnKey="name"            sortKey={deviceSort.sortKey} sortDir={deviceSort.sortDir} onSort={deviceSort.handleSort} />
                  <SortHeader label="Asset serial" columnKey="assetSerial"     sortKey={deviceSort.sortKey} sortDir={deviceSort.sortDir} onSort={deviceSort.handleSort} />
                  <SortHeader label="Serial"       columnKey="serial"          sortKey={deviceSort.sortKey} sortDir={deviceSort.sortDir} onSort={deviceSort.handleSort} />
                  <SortHeader label="Channel"      columnKey="updateChannel"   sortKey={deviceSort.sortKey} sortDir={deviceSort.sortDir} onSort={deviceSort.handleSort} />
                  <SortHeader label="Firmware" columnKey="firmwareVersion" sortKey={deviceSort.sortKey} sortDir={deviceSort.sortDir} onSort={deviceSort.handleSort} />
                  <SortHeader label="Paired"   columnKey="pairedAt"        sortKey={deviceSort.sortKey} sortDir={deviceSort.sortDir} onSort={deviceSort.handleSort} />
                  <SortHeader label="Last seen" columnKey="lastSeen"       sortKey={deviceSort.sortKey} sortDir={deviceSort.sortDir} onSort={deviceSort.handleSort} />
                </tr>
              </thead>
              <tbody>
                {deviceSort.sorted.map((d) => (
                  <tr key={d.id}>
                    <td>{d.name ?? <span className="muted">(unnamed)</span>}</td>
                    <td>{d.assetSerial ? <code>{d.assetSerial}</code> : <span className="muted">—</span>}</td>
                    <td><code>{formatSerial(d.serial)}</code></td>
                    <td>{d.updateChannel}</td>
                    <td>{d.firmwareVersion ?? <span className="muted">—</span>}</td>
                    <td>{new Date(d.pairedAt).toLocaleDateString()}</td>
                    <td>
                      {d.lastSeen
                        ? new Date(d.lastSeen).toLocaleString()
                        : <span className="muted">never</span>}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      <div className="card">
        <h3>Recent tests ({tests.data?.tests.length ?? "…"})</h3>
        {tests.isLoading && <p className="muted">Loading…</p>}
        {tests.error && (
          <p className="err">Couldn't load — {(tests.error as Error).message}</p>
        )}
        {tests.data && tests.data.tests.length === 0 && (
          <p className="muted">No tests yet.</p>
        )}
        {tests.data && tests.data.tests.length > 0 && (
          <div className="table-scroll">
            <table>
              <thead>
                <tr>
                  <SortHeader label="Started"  columnKey="startedAt"  sortKey={testSort.sortKey} sortDir={testSort.sortDir} onSort={testSort.handleSort} />
                  <SortHeader label="Type"     columnKey="testType"   sortKey={testSort.sortKey} sortDir={testSort.sortDir} onSort={testSort.handleSort} />
                  <SortHeader label="Result"   columnKey="testResult" sortKey={testSort.sortKey} sortDir={testSort.sortDir} onSort={testSort.handleSort} />
                  <SortHeader label="Duration" columnKey="durationMs" sortKey={testSort.sortKey} sortDir={testSort.sortDir} onSort={testSort.handleSort} />
                  <SortHeader label="Device"   columnKey="deviceId"   sortKey={testSort.sortKey} sortDir={testSort.sortDir} onSort={testSort.handleSort} />
                </tr>
              </thead>
              <tbody>
                {testSort.sorted.map((t) => (
                  <tr key={t.id}>
                    <td>{new Date(t.startedAt).toLocaleString()}</td>
                    <td>{testTypeLabel(t.testType)}</td>
                    <td><span className={testResultPillClass(t.testResult)}>{testResultLabel(t.testResult)}</span></td>
                    <td>{fmtDurationMs(t.durationMs)}</td>
                    <td title={t.deviceId}>
                      {t.deviceName
                        ? t.deviceName
                        : <code className="muted">{t.deviceId.slice(0, 10)}…</code>}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </>
  );
}
